/* Your presence: intentful mode + "now" line. Written to app.atrium.presence
   (account data) and projected by the Presence Engine. Real signal only. */
import { defineStore } from 'pinia'
import { matrix } from '@/lib/matrix'
import { isPresenceMode, type PresenceDoc, type PresenceMode } from '@/types/atrium'

const LS_KEY = 'atrium.presence.v1'

interface Saved { myMode: PresenceMode; myNow: string }

function loadSaved(): Saved {
  try {
    const s = JSON.parse(localStorage.getItem(LS_KEY) || '{}') as Partial<Saved>
    return {
      myMode: s.myMode && isPresenceMode(s.myMode) ? s.myMode : 'cowork',
      myNow: typeof s.myNow === 'string' ? s.myNow : '',
    }
  } catch {
    return { myMode: 'cowork', myNow: '' }
  }
}

export interface MyMedia { room: string | null; mic: boolean; cam: boolean; screen: boolean }

interface PresenceState {
  myMode: PresenceMode
  myNow: string
  myMedia: MyMedia | null
  /** Matrix room ID whose call ANOTHER of my devices/tabs is also in (null when
   *  this device is alone). Maintained by the call layer; floor.leaveRoom
   *  consults it so leaving on one device doesn't clear occupancy the other
   *  device still owns (m.rtc.member is keyed per user, not per device). */
  otherDeviceInCallRoom: string | null
  pushTimer: ReturnType<typeof setTimeout> | null
}

export const usePresence = defineStore('presence', {
  state: (): PresenceState => ({ ...loadSaved(), myMedia: null, otherDeviceInCallRoom: null, pushTimer: null }),

  getters: {
    isDnd: (s): boolean => s.myMode === 'deep' || s.myMode === 'break',
  },

  actions: {
    persist() {
      try {
        localStorage.setItem(LS_KEY, JSON.stringify({ myMode: this.myMode, myNow: this.myNow }))
      } catch { /* non-fatal */ }
    },
    setMode(mode: PresenceMode) {
      this.myMode = mode
      this.persist()
      this.schedulePush()
    },
    setNow(now: string) {
      this.myNow = now
      this.persist()
      this.schedulePush()
    },
    schedulePush() {
      if (this.pushTimer) clearTimeout(this.pushTimer)
      this.pushTimer = setTimeout(() => void this.push(), 350)
    },
    async push() {
      const myId = matrix.userId()
      if (!myId) return
      const doc: PresenceDoc = {
        mode: this.myMode, manual: true, now: this.myNow || '',
        since: Date.now(), source: 'manual',
      }
      try {
        await matrix.setAccountData('app.atrium.presence', doc)
      } catch (e) {
        console.warn('[presence] account data write failed', e)
      }
      // Ask the Presence Engine to project it (single writer of presence.member).
      const token = matrix.getSession()?.accessToken
      if (!token) return
      try {
        await fetch('/atrium/api/presence', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
          body: JSON.stringify({
            user_id: myId,
            presence: doc,
            visibility: { default: 'full', now_line: 'team' },
          }),
        })
      } catch {
        /* engine unreachable — account data is still set */
      }
    },
  },
})
